import LoginInput from "@/components/input/LoginInput";
import ButtonSpinner from "@/components/spinner/ButtonSpinner";
import ErrorTooltip from "@/components/tooltip/ErrorTooltip";
import { Button } from "@/components/ui/button";
import useOneTimeLogin from "@/hooks/useOneTimeLogin";
import { sendContentsLog, withLog } from "@/lib/a2s";
import { getAccessCode, handleEnterKey, isNumberPressed } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { getIsGetoAccessCode } from "@/lib/geto";
import { ACCESS_CODE_SEARCH_PARAM } from "@/consts";

const IS_NOT_NUMBER = "숫자만 입력 가능합니다.";
const INPUT_LOGIN_NUMBER = "일회용 로그인 번호를 입력해주세요.";

const OneTimeLogin = () => {
  const regex = /^[0-9]*$/;

  const { signInOtp, errorCode } = useOneTimeLogin();
  const isGeto = getIsGetoAccessCode();

  const [buttonLoading, setButtonLoading] = useState(false);

  const [otpNum1, setOtpNum1] = useState<string>("");
  const [otpNum2, setOtpNum2] = useState<string>("");

  const [isValidate1, setIsValidate1] = useState<boolean>(true);
  const [isValidate2, setIsValidate2] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>(IS_NOT_NUMBER);

  const handleInvalidate = (message: string, order: number) => {
    const setIsValidate = order === 1 ? setIsValidate1 : setIsValidate2;
    setIsValidate(false);
    setErrorMessage(message ?? IS_NOT_NUMBER);
  };

  const checkValidate = () => {
    let isValid = true;
    if (otpNum1.length !== 4) {
      handleInvalidate(INPUT_LOGIN_NUMBER, 1);
      isValid = false;
    } else if (!regex.test(otpNum1)) {
      handleInvalidate(IS_NOT_NUMBER, 1);
      isValid = false;
    }

    if (otpNum2.length !== 4) {
      handleInvalidate(INPUT_LOGIN_NUMBER, 2);
      isValid = false;
    } else if (!regex.test(otpNum2)) {
      handleInvalidate(IS_NOT_NUMBER, 2);
      isValid = false;
    }

    return isValid;
  };

  const handleChangeValue = (val: string, order: number) => {
    const trimmedVal = val.trim();
    const setOtpNum = order === 1 ? setOtpNum1 : setOtpNum2;
    const setIsValidate = order === 1 ? setIsValidate1 : setIsValidate2;

    if (!regex.test(trimmedVal)) {
      handleInvalidate(IS_NOT_NUMBER, order);
      return;
    }

    setIsValidate(true);
    if (trimmedVal.length <= 4) {
      setOtpNum(trimmedVal);
    }
  };

  const handleLogin = () => {
    if (checkValidate()) {
      const otp = otpNum1 + otpNum2;
      signInOtp(otp, setButtonLoading);
    }
  };

  const handleBlockKey = (e: React.SyntheticEvent, order: number) => {
    const setIsValidate = order === 1 ? setIsValidate1 : setIsValidate2;

    if (isNumberPressed(e)) {
      setIsValidate(true);
    } else {
      handleInvalidate(IS_NOT_NUMBER, order);
      e.preventDefault();
    }
  };

  const handleNexonPlayClick = () => {
    const baseUrl = isMobile ? import.meta.env.VITE_NEXON_PLAY_MOBILE_URL : import.meta.env.VITE_NEXON_PLAY_PC_URL;
    const url = new URL(baseUrl);

    if (isGeto) {
      const accessCode = getAccessCode();
      url.searchParams.set(ACCESS_CODE_SEARCH_PARAM, accessCode.toString());
    }

    window.location.href = url.toString();
  };

  useEffect(() => {
    sendContentsLog("LOGIN0016");
  }, []);

  return (
    <div className="flex w-full flex-col items-center break-keep">
      <p className="mb-7 mt-8 text-center text-base text-gray-900 md:mb-8 md:mt-9">
        넥슨플레이 &gt; 넥슨인증기 &gt; 일회용 로그인에서 8자리 숫자를 입력해주세요.
      </p>

      <ErrorTooltip errorCode={errorCode} className="mb-3" />

      <div className="flex w-full space-x-3">
        <LoginInput
          maxLength={4}
          className="flex-1"
          type="oneTimeLogin"
          value={otpNum1}
          setValue={(val) => handleChangeValue(val, 1)}
          isError={!isValidate1}
          errorMessage={errorMessage}
          onBeforeInput={(e) => handleBlockKey(e, 1)}
          onKeyDown={(e) => handleEnterKey(e, handleLogin)}
          autoComplete="new-password"
        />
        <LoginInput
          maxLength={4}
          className="flex-1"
          type="oneTimeLogin"
          value={otpNum2}
          setValue={(val) => handleChangeValue(val, 2)}
          isError={!isValidate2}
          errorMessage={isValidate1 ? errorMessage : ""}
          onBeforeInput={(e) => handleBlockKey(e, 2)}
          onKeyDown={(e) => handleEnterKey(e, handleLogin)}
          autoComplete="new-password"
        />
      </div>

      <Button className="my-8 font-medium md:my-9" onClick={withLog("LOGIN0017", handleLogin)}>
        <span className="relative">
          <ButtonSpinner loading={buttonLoading} />
          일회용 로그인
        </span>
      </Button>

      <div className="mb-1.5 flex w-full justify-between text-sm text-gray-700">
        <p className="font-bold">일회용 로그인 이용 가이드</p>
        <Button
          variant="link"
          className="size-fit gap-0 p-0 font-medium [&_svg]:size-3.5"
          onClick={withLog("LOGIN0018", handleNexonPlayClick)}
        >
          넥슨플레이 안내
          <ChevronRight />
        </Button>
      </div>

      <ul className="relative w-full list-outside list-none ps-2 text-sm text-gray-600">
        <li className="pl-1 before:absolute before:left-0 before:content-['※']">넥슨플레이 앱을 실행시켜주세요.</li>
        <li className="pl-1 before:absolute before:left-0 before:content-['※']">
          메뉴 탭에서 [넥슨인증기] 메뉴를 클릭, [일회용 로그인] 버튼을 클릭해주세요.
        </li>
        <li className="pl-1 before:absolute before:left-0 before:content-['※']">8자리 번호를 입력하고 로그인 버튼을 클릭해주세요.</li>
      </ul>
    </div>
  );
};

export default OneTimeLogin;
